import {readContributionActionRequest} from '../contribution-action-requests.js';
import {readContributionActionResponse} from '../contribution-responses.js';
import {treasuryIdempotencyKey} from '../treasury-write-request.js';
import {readTreasurySettingsActionRequest} from '../treasury-settings-requests.js';
import {readTreasurySettingsActionResponse} from '../treasury-settings-responses.js';
import {readAccountingActionRequest, accountingIdempotencyKey} from '../accounting-action-requests.js';
import {readAccountingActionResponse} from '../accounting-responses.js';
// Shared treasury domain client.
//
// Owns the `/api/treasury/*` paths plus the treasury-adjacent budget endpoints
// (`/api/budgets/current`, `/api/budget-allocations/*`) that the Guardian web
// workspace drives. Web (liquid-spirit-app) uses the client for every call;
// Guardian web keeps its cacheFetch GETs (using the exported path constants) and
// routes mutations through the client; Guardian mobile keeps its requestJson
// transport + normalizers and uses the exported path constants/builders.
//
// The adapter mirrors the createApiClient method surface:
//   get(path, options), post(path, body, options),
//   put(path, body, options), patch(path, body, options), delete(path, options)
//
// Body divergence (e.g. fund-code normalization differs between web and
// Guardian) stays caller-side: the client owns paths/methods and posts whatever
// payload the caller builds.

import {readTreasuryExpenseListResponse, readTreasuryExpenseResponse} from './treasury-expense-responses.js';
import {readExpenseId, readExpenseQuery, readExpenseRequest} from '../expense-requests.js';
import {createBudgetsClient} from './budgets.js';
import {readBudgetRevisionActionRequest} from '../budget-revision-requests.js';
import {readTreasuryFundListResponse, readTreasuryFundMutationResponse, readTreasuryFundPostingResponse} from './treasury-fund-responses.js';
import {readTreasuryTransactionsQuery} from '../treasury-transaction-requests.js';
export {TreasuryTransactionRequestContractError, readTreasuryTransactionsQuery} from '../treasury-transaction-requests.js';
import {readPaymentIntentIdentifier, readPaymentIntentQuery} from '../treasury-payment-intent-requests.js';
export {TreasuryPaymentIntentRequestContractError, readPaymentIntentIdentifier, readPaymentIntentQuery} from '../treasury-payment-intent-requests.js';
import {readTreasuryPostingRequest} from '../treasury-posting-requests.js';
import {readFundCreateRequest, readFundReference, readFundDonationConfigRequest, readFundReconciliationReviewRequest} from '../treasury-fund-requests.js';
import {readTreasuryWalletIdentifier, readTreasuryWalletCreateRequest, readTreasuryWalletUpdateRequest} from '../treasury-wallet-requests.js';
import {readTreasuryWalletListResponse, readTreasuryWalletMutationResponse, readTreasuryWalletSyncResponse} from './treasury-wallet-responses.js';
import {readTreasuryPaymentIntentListResponse, readTreasuryPaymentIntentResponse} from './treasury-payment-intent-responses.js';
export {TreasuryExpenseResponseContractError, readTreasuryExpenseListResponse, readTreasuryExpenseResponse, readTreasuryExpenseRecoveryResponse} from './treasury-expense-responses.js';
export {TreasuryFundResponseContractError, readTreasuryFundListResponse, readTreasuryFundMutationResponse, readTreasuryFundPostingResponse} from './treasury-fund-responses.js';
export {TreasuryPostingRequestContractError, readTreasuryPostingRequest} from '../treasury-posting-requests.js';
export {TreasuryFundRequestContractError, readFundCreateRequest, readFundReference, readFundDonationConfigRequest, readFundReconciliationReviewRequest} from '../treasury-fund-requests.js';
export {TreasuryWalletRequestContractError, readTreasuryWalletIdentifier, readTreasuryWalletCreateRequest, readTreasuryWalletUpdateRequest} from '../treasury-wallet-requests.js';
export {TreasuryWalletResponseContractError, readTreasuryWalletListResponse, readTreasuryWalletMutationResponse, readTreasuryWalletSyncResponse} from './treasury-wallet-responses.js';
export {TreasuryPaymentIntentResponseContractError, readTreasuryPaymentIntentListResponse, readTreasuryPaymentIntentResponse} from './treasury-payment-intent-responses.js';

export const TREASURY_PATHS = {
  funds: '/api/treasury/funds',
  transactions: '/api/treasury/transactions',
  wallets: '/api/treasury/wallets',
  walletsSync: '/api/treasury/wallets/sync',
  paymentIntents: '/api/treasury/payment-intents',
  currentBudget: '/api/budgets/current',
  expenses: '/api/treasury/expenses',
};

const enc = (value) => encodeURIComponent(value);

export const withLimit = (path, limit) =>
  limit ? `${path}?limit=${enc(limit)}` : path;

export const buildExpensePath = id => `${TREASURY_PATHS.expenses}/${enc(id)}`;
export const buildExpenseActionPath = (id, action) => `${buildExpensePath(id)}/actions/${enc(action)}`;
export const buildExpenseReceiptPath = (id, receiptId) => `${buildExpensePath(id)}/receipts${receiptId ? `/${enc(receiptId)}` : ''}`;

// Fund-scoped paths
export const buildFundPath = (fundId) => `${TREASURY_PATHS.funds}/${enc(fundId)}`;
export const buildFundAddPath = (fundId) => `${buildFundPath(fundId)}/add`;
export const buildFundBalancePath = (fundId) => `${buildFundPath(fundId)}/balance`;
export const buildFundTransactionPath = (fundId) => `${buildFundPath(fundId)}/transaction`;
export const buildFundTransactionsPath = (fundId) => `${buildFundPath(fundId)}/transactions`;
export const buildFundWalletsPath = (fundId) => `${buildFundPath(fundId)}/wallets`;
export const buildFundWalletsSyncPath = (fundId) => `${buildFundWalletsPath(fundId)}/sync`;
export const buildFundReconciliationReviewPath = (fundId) =>
  `${buildFundPath(fundId)}/reconciliation-review`;
export const buildFundDonationConfigPath = (fundId) =>
  `${buildFundPath(fundId)}/donation-config`;

// Wallet-scoped paths
export const buildWalletPath = (walletId) => `${TREASURY_PATHS.wallets}/${enc(walletId)}`;
export const buildWalletSyncPath = (walletId) => `${buildWalletPath(walletId)}/sync`;

// Payment-intent paths
export const buildPaymentIntentSubmitPath = (intentId) =>
  `${TREASURY_PATHS.paymentIntents}/${enc(intentId)}/submit`;
export const buildPaymentIntentReviewPath = (intentId) =>
  `${TREASURY_PATHS.paymentIntents}/${enc(intentId)}/review`;

export class TreasuryTransactionsResponseContractError extends Error {
  constructor() {
    super('Invalid Treasury transactions response: expected a success envelope with valid transaction records.');
    this.name = 'TreasuryTransactionsResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}
const transactionSecretFree = value => !['password', 'passwordHash', 'accessToken', 'refreshToken', 'resetPasswordToken', 'emailVerificationCode'].some(key => Object.hasOwn(value, key));
const isTransactionRecord = row => row !== null && typeof row === 'object' && !Array.isArray(row) && transactionSecretFree(row)
  && typeof row.id === 'string' && row.id.length > 0 && typeof row.fundId === 'string' && row.fundId.length > 0
  && (row.fundName === null || typeof row.fundName === 'string') && (row.fundCode === null || typeof row.fundCode === 'string')
  && typeof row.amount === 'number' && Number.isFinite(row.amount) && typeof row.note === 'string'
  && typeof row.referenceType === 'string' && ['donation', 'refund', 'advance', 'advance_return', 'allocation', 'expense', 'transfer', 'reconciliation', 'manual', 'onchain_transfer'].includes(row.type)
  && ['pending', 'confirmed', 'failed', 'reconciled', 'observed'].includes(row.status) && ['inbound', 'outbound', 'internal'].includes(row.direction)
  && (row.assetSymbol === null || typeof row.assetSymbol === 'string') && (row.chain === null || typeof row.chain === 'string')
  && (row.walletId === null || typeof row.walletId === 'string') && (row.walletAddress === null || typeof row.walletAddress === 'string')
  && (row.counterpartyAddress === null || typeof row.counterpartyAddress === 'string') && (row.txHash === null || typeof row.txHash === 'string')
  && (row.blockNumber === null || typeof row.blockNumber === 'number' && Number.isFinite(row.blockNumber))
  && ['manual', 'blockchain_observer', 'allocation_engine', 'admin_adjustment', 'wallet_sync'].includes(row.source)
  && row.metadata !== null && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
  && (row.fiatValueSnapshot === null || typeof row.fiatValueSnapshot === 'number' && Number.isFinite(row.fiatValueSnapshot))
  && (row.fiatCurrencySnapshot === null || typeof row.fiatCurrencySnapshot === 'string')
  && (row.createdBy === null || row.createdBy !== null && typeof row.createdBy === 'object' && !Array.isArray(row.createdBy) && transactionSecretFree(row.createdBy) && typeof row.createdBy.id === 'string' && typeof row.createdBy.name === 'string'
    && (row.createdBy.email === null || typeof row.createdBy.email === 'string'))
  && (typeof row.createdAt === 'string' || row.createdAt instanceof Date) && Number.isFinite(Date.parse(row.createdAt))
  && (typeof row.updatedAt === 'string' || row.updatedAt instanceof Date) && Number.isFinite(Date.parse(row.updatedAt));
export const readTreasuryTransactionsResponse = payload => {
  if (payload?.success !== true || !Array.isArray(payload.data) || !payload.data.every(isTransactionRecord)) {
    throw new TreasuryTransactionsResponseContractError();
  }
  return payload;
};

// Budget-allocation paths
export const buildBudgetAllocationApprovePath = (allocationId) =>
  `/api/budget-allocations/${enc(allocationId)}/approve`;
export const buildBudgetAllocationDeclinePath = (allocationId) =>
  `/api/budget-allocations/${enc(allocationId)}/decline`;

// List builders (own the limit/fund-scoping query shape)
export const buildTransactionsPath = ({ fundId, limit } = {}) =>
  withLimit(fundId ? buildFundTransactionsPath(fundId) : TREASURY_PATHS.transactions, limit);
export const buildWalletsListPath = ({ fundId } = {}) =>
  fundId ? buildFundWalletsPath(fundId) : TREASURY_PATHS.wallets;
export const buildPaymentIntentsPath = ({ limit } = {}) =>
  withLimit(TREASURY_PATHS.paymentIntents, limit);

export const createTreasuryClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createTreasuryClient requires a transport adapter.');
  }

  const pendingWrites = new Map();
  const write = (method, path, payload, options, validate = value => value) => {
    const key = JSON.stringify([options.token || null, options.params?.communityId || options.communityId || null, method, path, payload]);
    let pending = pendingWrites.get(key);
    if (pending?.promise) return pending.promise;
    if (!pending) {
      const requestId = payload?.requestId || globalThis.crypto?.randomUUID?.()
        || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      pending = { requestId };
      pendingWrites.set(key, pending);
    }
    // Preserve the key after an uncertain failure. A deliberate new operation
    // following a confirmed success receives a new key. Nothing is persisted.
    pending.promise = Promise.resolve().then(() => adapter[method](path, { ...payload, requestId: pending.requestId }, options))
      .then((result) => {
        const validated = validate(result);
        pendingWrites.delete(key);
        return validated;
      }).catch((error) => {
        pending.promise = null;
        const status = error?.status || error?.statusCode;
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) pendingWrites.delete(key);
        throw error;
      });
    return pending.promise;
  };

  return {
    actOnBudgetRevision(action, payload, options = {}) { readBudgetRevisionActionRequest(action, payload); return write('post', `/api/treasury/budget-revisions/actions/${enc(action)}`, payload, options); },
    actOnTreasurySettings(action, payload, options = {}) { readTreasurySettingsActionRequest(action, payload); return write('post', `/api/treasury/settings/actions/${enc(action)}`, payload, options, readTreasurySettingsActionResponse); },
    actOnContribution(action, payload, options = {}) {
      readContributionActionRequest(action, payload, options);
      const requestId = treasuryIdempotencyKey(payload, options);
      return write('post', `/api/treasury/contributions/actions/${enc(action)}`, requestId ? {...payload, requestId} : payload, options, result => readContributionActionResponse(action, result));
    },
    actOnAccounting(action, payload, options = {}) {
      readAccountingActionRequest(action, payload, options);
      const requestId = accountingIdempotencyKey(payload, options);
      return write('post', `/api/treasury/accounting/${enc(action)}`, requestId ? {...payload, requestId} : payload, options, result => readAccountingActionResponse(action, result));
    },
    listExpenses(input = {}, options = {}) {
      const {before, view} = readExpenseQuery(input);
      const params = [before ? `before=${enc(before)}` : '', view ? `view=${enc(view)}` : ''].filter(Boolean).join('&');
      return adapter.get(`${TREASURY_PATHS.expenses}${params ? `?${params}` : ''}`, options).then(readTreasuryExpenseListResponse);
    },
    getExpense(id, options = {}) { readExpenseId(id); return adapter.get(buildExpensePath(id), options).then(result => readTreasuryExpenseResponse(result, id)); },
    createExpense(payload, options = {}) { readExpenseRequest('create', payload, options); const requestId = treasuryIdempotencyKey(payload, options); return write('post', TREASURY_PATHS.expenses, requestId ? {...payload, requestId} : payload, options, readTreasuryExpenseResponse); },
    actOnExpense(id, action, payload, options = {}) { readExpenseId(id); readExpenseRequest(action, payload, options); const requestId = treasuryIdempotencyKey(payload, options); return write('post', buildExpenseActionPath(id, action), requestId ? {...payload, requestId} : payload, options, result => readTreasuryExpenseResponse(result, id)); },

    // Funds
    listFunds(options = {}) {
      return adapter.get(TREASURY_PATHS.funds, options).then(readTreasuryFundListResponse);
    },
    createFund(payload, options = {}) {
      return adapter.post(TREASURY_PATHS.funds, readFundCreateRequest(payload), options).then(readTreasuryFundMutationResponse);
    },
    addFundBalance(fundId, payload, options = {}) {
      return write('post', buildFundAddPath(readFundReference(fundId)), readTreasuryPostingRequest(payload, 'add'), options, readTreasuryFundPostingResponse);
    },
    setFundBalance(fundId, payload, options = {}) {
      return write('put', buildFundBalancePath(readFundReference(fundId)), readTreasuryPostingRequest(payload, 'set_balance'), options, readTreasuryFundPostingResponse);
    },
    registerFundTransaction(fundId, payload, options = {}) {
      return write('post', buildFundTransactionPath(readFundReference(fundId)), readTreasuryPostingRequest(payload, 'transaction'), options, readTreasuryFundPostingResponse);
    },
    setFundReconciliationReview(fundId, payload, options = {}) {
      return adapter.put(buildFundReconciliationReviewPath(readFundReference(fundId)), readFundReconciliationReviewRequest(payload), options).then(readTreasuryFundMutationResponse);
    },
    setFundDonationConfig(fundId, donationConfig, options = {}) {
      return adapter.put(buildFundDonationConfigPath(readFundReference(fundId)), readFundDonationConfigRequest(donationConfig), options).then(readTreasuryFundMutationResponse);
    },

    // Transactions
    listTransactions({ fundId, limit } = {}, options = {}) {
      readTreasuryTransactionsQuery({limit});
      if (fundId !== undefined && fundId !== null && fundId !== '') readFundReference(fundId);
      return adapter.get(buildTransactionsPath({ fundId, limit }), options).then(readTreasuryTransactionsResponse);
    },

    // Wallets
    listWallets({ fundId } = {}, options = {}) {
      if (fundId !== undefined && fundId !== null && fundId !== '') readFundReference(fundId);
      return adapter.get(buildWalletsListPath({ fundId }), options).then(readTreasuryWalletListResponse);
    },
    createWallet(fundId, payload, options = {}) {
      return adapter.post(buildFundWalletsPath(readFundReference(fundId)), readTreasuryWalletCreateRequest(payload), options).then(readTreasuryWalletMutationResponse);
    },
    syncWallet(walletId, options = {}) {
      return adapter.post(buildWalletSyncPath(readTreasuryWalletIdentifier(walletId)), undefined, options).then(readTreasuryWalletSyncResponse);
    },
    updateWallet(walletId, updates, options = {}) {
      return adapter.put(buildWalletPath(readTreasuryWalletIdentifier(walletId)), readTreasuryWalletUpdateRequest(updates), options).then(readTreasuryWalletMutationResponse);
    },
    archiveWallet(walletId, options = {}) {
      return adapter.delete(buildWalletPath(readTreasuryWalletIdentifier(walletId)), options).then(readTreasuryWalletMutationResponse);
    },
    syncFundWallets(fundId, options = {}) {
      return adapter.post(buildFundWalletsSyncPath(readFundReference(fundId)), undefined, options).then(readTreasuryWalletSyncResponse);
    },
    syncCommunityWallets(options = {}) {
      return adapter.post(TREASURY_PATHS.walletsSync, undefined, options).then(readTreasuryWalletSyncResponse);
    },

    // Payment intents
    createPaymentIntent(payload, options = {}) {
      return adapter.post(TREASURY_PATHS.paymentIntents, payload, options).then(readTreasuryPaymentIntentResponse);
    },
    listPaymentIntents({ limit } = {}, options = {}) {
      readPaymentIntentQuery({limit});
      return adapter.get(buildPaymentIntentsPath({ limit }), options).then(readTreasuryPaymentIntentListResponse);
    },
    getPaymentIntent(intentId, options = {}) {
      readPaymentIntentIdentifier(intentId);
      return adapter.get(`${TREASURY_PATHS.paymentIntents}/${enc(intentId)}`, options).then(readTreasuryPaymentIntentResponse);
    },
    submitPaymentIntent(intentId, payload, options = {}) {
      return adapter.post(buildPaymentIntentSubmitPath(intentId), payload, options).then(readTreasuryPaymentIntentResponse);
    },
    reviewPaymentIntent(intentId, payload, options = {}) {
      return adapter.put(buildPaymentIntentReviewPath(intentId), payload, options).then(readTreasuryPaymentIntentResponse);
    },

    // Budgets / allocations
    fetchCurrentBudget(options = {}) {
      return createBudgetsClient(adapter).getCurrent(options);
    },
    approveAllocation(allocationId, payload, options = {}) {
      return createBudgetsClient(adapter).approveAllocationRequest(allocationId, payload, options);
    },
    declineAllocation(allocationId, payload, options = {}) {
      return createBudgetsClient(adapter).declineAllocationRequest(allocationId, payload, options);
    },
  };
};

// Namespace for durable request recovery storage, not a callable endpoint.
export const TREASURY_RECOVERY_SCOPE_PATH = '/api/treasury';
